"use client";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Quiz, QuizQuestion } from "@/lib/types/quiz";
import { ArrowLeft, Brain, CheckCircle, XCircle, Trophy, RotateCcw } from "lucide-react";
import Link from "next/link";
import { useState } from "react";

interface QuizState {
  currentQuestionIndex: number;
  answers: Record<string, string>;
  isCompleted: boolean;
  score: number;
  totalQuestions: number;
}

interface QuizSolverProps {
  quiz: Quiz;
  onFinish?: (result: { score: number; totalQuestions: number; percentage: number; isPassing: boolean }) => void;
  showBackButton?: boolean;
  backButtonHref?: string;
  backButtonText?: string;
  showRetakeButton?: boolean;
  showTryAnotherButton?: boolean;
  tryAnotherHref?: string;
  compact?: boolean;
}

export default function QuizSolver({
  quiz,
  onFinish,
  showBackButton = true,
  backButtonHref = "/quizzes",
  backButtonText = "Back to Quizzes",
  showRetakeButton = true,
  showTryAnotherButton = true,
  tryAnotherHref = "/quizzes",
  compact = false,
}: QuizSolverProps) {
  const [quizState, setQuizState] = useState<QuizState>({
    currentQuestionIndex: 0,
    answers: {},
    isCompleted: false,
    score: 0,
    totalQuestions: quiz.questions.length,
  });

  const handleAnswerSelect = (questionId: string, optionId: string) => {
    setQuizState(prev => ({
      ...prev,
      answers: {
        ...prev.answers,
        [questionId]: optionId,
      },
    }));
  };

  const handleNextQuestion = () => {
    if (quizState.currentQuestionIndex < quiz.questions.length - 1) {
      setQuizState(prev => ({
        ...prev,
        currentQuestionIndex: prev.currentQuestionIndex + 1,
      }));
    }
  };

  const handlePreviousQuestion = () => {
    if (quizState.currentQuestionIndex > 0) {
      setQuizState(prev => ({
        ...prev,
        currentQuestionIndex: prev.currentQuestionIndex - 1,
      }));
    }
  };

  const handleSubmitQuiz = () => {
    let score = 0;
    quiz.questions.forEach(question => {
      const selectedAnswer = quizState.answers[question.id];
      if (selectedAnswer) {
        const selectedOption = question.options.find(option => option.id === selectedAnswer);
        if (selectedOption?.isCorrect) {
          score += 1;
        }
      }
    });

    const percentage = Math.round((score / quizState.totalQuestions) * 100);
    const isPassing = percentage >= 70;

    setQuizState(prev => ({
      ...prev,
      isCompleted: true,
      score,
    }));

    // Call onFinish callback if provided
    if (onFinish) {
      onFinish({ score, totalQuestions: quizState.totalQuestions, percentage, isPassing });
    }
  };

  const handleRetakeQuiz = () => {
    setQuizState({
      currentQuestionIndex: 0,
      answers: {},
      isCompleted: false,
      score: 0,
      totalQuestions: quiz.questions.length,
    });
  };

  const currentQuestion = quiz.questions[quizState.currentQuestionIndex];
  const progress = ((quizState.currentQuestionIndex + 1) / quizState.totalQuestions) * 100;
  const answeredQuestions = Object.keys(quizState.answers).length;

  if (quizState.isCompleted) {
    const percentage = Math.round((quizState.score / quizState.totalQuestions) * 100);
    const isPassing = percentage >= 70;

    return (
      <div className={compact ? "" : "container mx-auto px-4 py-8"}>
        <div className={compact ? "" : "max-w-2xl mx-auto"}>
          {showBackButton && !compact && (
            <div className="mb-8">
              <Link
                href={backButtonHref}
                className="inline-flex items-center text-gray-600 hover:text-green-600 transition-colors"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                {backButtonText}
              </Link>
            </div>
          )}

          <Card className="text-center">
            <CardHeader>
              <div className="flex justify-center mb-4">
                {isPassing ? (
                  <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center">
                    <Trophy className="h-10 w-10 text-green-600" />
                  </div>
                ) : (
                  <div className="w-20 h-20 rounded-full bg-orange-100 flex items-center justify-center">
                    <Brain className="h-10 w-10 text-orange-600" />
                  </div>
                )}
              </div>
              <CardTitle className="text-2xl font-bold text-gray-900">
                Quiz Completed!
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <p className="text-lg text-gray-600 mb-2">
                  Your Score: <span className="font-bold text-green-600">{quizState.score}/{quizState.totalQuestions}</span>
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {percentage}%
                </p>
                <Badge 
                  variant={isPassing ? "default" : "secondary"}
                  className="mt-2"
                >
                  {isPassing ? "Passed!" : "Keep Learning!"}
                </Badge>
              </div>

              <div className="space-y-3">
                {showRetakeButton && (
                  <Button onClick={handleRetakeQuiz} className="w-full">
                    <RotateCcw className="h-4 w-4 mr-2" />
                    Retake Quiz
                  </Button>
                )}
                {showTryAnotherButton && (
                  <Button variant="outline" className="w-full" asChild>
                    <Link href={tryAnotherHref}>
                      Try Another Quiz
                    </Link>
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className={compact ? "" : "container mx-auto px-4 py-8"}>
      <div className={compact ? "" : "max-w-4xl mx-auto"}>
        {showBackButton && !compact && (
          <div className="mb-8">
            <Link
              href={backButtonHref}
              className="inline-flex items-center text-gray-600 hover:text-green-600 transition-colors"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              {backButtonText}
            </Link>
          </div>
        )}

        {/* Quiz Header */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-2xl font-bold text-gray-900">
                  {quiz.title}
                </CardTitle>
                <p className="text-gray-600 mt-2">
                  {quiz.description}
                </p>
              </div>
              <div className="text-right">
                <Badge variant="secondary">
                  Question {quizState.currentQuestionIndex + 1} of {quizState.totalQuestions}
                </Badge>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between text-sm text-gray-600">
                <span>Progress</span>
                <span>{answeredQuestions}/{quizState.totalQuestions} answered</span>
              </div>
              <Progress value={progress} className="h-2" />
            </div>
          </CardContent>
        </Card>

        {/* Question */}
        {currentQuestion && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-gray-900">
                {currentQuestion.content}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <RadioGroup
                value={quizState.answers[currentQuestion.id] || ""}
                onValueChange={(value) => handleAnswerSelect(currentQuestion.id, value)}
                className="space-y-3"
              >
                {currentQuestion.options.map((option) => (
                  <div key={option.id} className="flex items-center space-x-2">
                    <RadioGroupItem value={option.id} id={option.id} />
                    <Label htmlFor={option.id} className="text-base cursor-pointer">
                      {option.content}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </CardContent>
          </Card>
        )}

        {/* Navigation */}
        <div className="flex justify-between mt-6">
          <Button
            variant="outline"
            onClick={handlePreviousQuestion}
            disabled={quizState.currentQuestionIndex === 0}
          >
            Previous
          </Button>

          <div className="flex gap-2">
            {quizState.currentQuestionIndex === quizState.totalQuestions - 1 ? (
              <Button 
                onClick={handleSubmitQuiz}
                disabled={answeredQuestions < quizState.totalQuestions}
              >
                Submit Quiz
              </Button>
            ) : (
              <Button 
                onClick={handleNextQuestion}
                disabled={!quizState.answers[currentQuestion?.id || ""]}
              >
                Next
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
} 